import { Site, SiteAggregateData } from '@domain/graphql.types';
import { alpha, Box, CircularProgress, Stack, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SiteLookupContext from '../context/SiteLookupContext';
import SiteLookupMapContext from '../context/SiteLookupMapContext';
import { getAvailableSiteLookupDataViews, SiteLookupDataView, SiteLookupDataViewValue } from '../siteLookupDataView';
import ListCard from './ListCard';
import OverviewCard from './OverviewCard';

type Props = {
  dataView: SiteLookupDataView;
  hasSearchResult: boolean;
  fetching: boolean;
  sites: readonly Site[];
  totalCount: number;
  trackedPortsCount: number;
  aggregateData?: SiteAggregateData | null;
};

const SiteLookupList = ({ dataView, hasSearchResult, fetching, sites, totalCount, trackedPortsCount, aggregateData }: Props) => {
  const selectedCardRef = useRef<HTMLDivElement | null>(null);

  const { selectedSite } = useContext(SiteLookupMapContext);
  const { hasHistoricalAccess, hasRealTimeAccess, setDataView } = useContext(SiteLookupContext);

  const { t } = useTranslation();

  const { palette } = useTheme();

  useEffect(() => {
    if (!selectedSite || selectedSite.origin === 'list' || !selectedCardRef?.current) {
      return;
    }

    selectedCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectedSite]);

  const trackedPortsText = useMemo(
    () => (trackedPortsCount > 0 ? <>&nbsp;{t('siteLookup.list.trackedPorts', { count: trackedPortsCount })}</> : ''),
    [t, trackedPortsCount],
  );

  const availableDataViews = useMemo(
    () => getAvailableSiteLookupDataViews({ hasHistoricalAccess, hasRealTimeAccess }),
    [hasHistoricalAccess, hasRealTimeAccess],
  );
  const hasHistoricalView = useMemo(
    () => hasHistoricalAccess && dataView === SiteLookupDataViewValue.historical && sites.some((site) => !!site.historicalView),
    [dataView, hasHistoricalAccess, sites],
  );
  const canSwitchView = availableDataViews.length > 1;

  return (
    <Stack
      sx={{
        height: '100%',
        width: '100%',
        minHeight: 'calc(100vh - (var(--top-bar-height) + var(--search-bar-height)))',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        paddingBottom: 6,
        backgroundColor: palette.common.white,
      }}
    >
      {hasSearchResult && !fetching && (
        <>
          <Box
            alignItems={{ xs: 'stretch', md: 'center' }}
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            gap={1.5}
            justifyContent="space-between"
            marginBlockEnd={2}
            marginBlockStart={2.5}
            paddingX={1.5}
          >
            <Box>
              <Typography fontSize={20} fontWeight={700} lineHeight={1.15}>
                {t('siteLookup.list.sitesFound', { count: totalCount })}
              </Typography>
              <Typography color="text.secondary" fontSize={13} fontWeight={500} marginTop={0.35}>
                {trackedPortsText}
              </Typography>
            </Box>

            {canSwitchView && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={dataView}
                onChange={(_, nextValue: SiteLookupDataView | null) => nextValue && setDataView(nextValue)}
                sx={{
                  alignSelf: { xs: 'flex-start', md: 'center' },
                  backgroundColor: alpha(palette.primary.main, 0.05),
                  borderRadius: 999,
                  padding: 0.5,
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 0,
                    borderRadius: '999px !important',
                    color: palette.text.secondary,
                    fontSize: 12,
                    fontWeight: 700,
                    minWidth: 104,
                    px: 1.75,
                    py: 0.9,
                    textTransform: 'none',
                  },
                  '& .Mui-selected': {
                    backgroundColor: palette.common.white,
                    boxShadow: '0 2px 10px rgb(20 24 40 / 10%)',
                    color: palette.text.primary,
                  },
                }}
              >
                {availableDataViews.includes(SiteLookupDataViewValue.historical) && (
                  <ToggleButton value="historical">{t('siteLookup.viewToggle.historical')}</ToggleButton>
                )}
                {availableDataViews.includes(SiteLookupDataViewValue.realtime) && (
                  <ToggleButton value="realtime">{t('siteLookup.viewToggle.realtime')}</ToggleButton>
                )}
              </ToggleButtonGroup>
            )}
          </Box>

          {hasHistoricalView && <OverviewCard sites={sites} aggregateData={aggregateData} />}

          {sites.map((site, index) => (
            <ListCard ref={site.id === selectedSite?.id ? selectedCardRef : null} key={site.id} isLast={index === sites.length - 1} site={site} />
          ))}

          {totalCount > 0 && (
            <Typography fontSize={14} fontWeight={500} paddingX={1.5} marginBlockStart={5} textAlign="center">
              {t('siteLookup.list.endReached')}
            </Typography>
          )}
        </>
      )}

      {fetching && <CircularProgress sx={{ alignSelf: 'center', marginTop: 32 }} />}
    </Stack>
  );
};

export default SiteLookupList;
